import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, Loader2, Edit2, Trash2, ArrowRight, ClipboardList, TrendingUp, ShieldCheck, ShieldAlert, Search } from "lucide-react";
import OrgClienteUsers from "@/components/OrgClienteUsers";
import AtecoCombobox from "@/components/AtecoCombobox";
import { AddressFields } from "@/components/AddressFields";
import { ContactFields } from "@/components/ContactFields";
import { toast } from "sonner";
import { hasRole } from "@/lib/roles";
import { plural } from "@/lib/plural";
import { isValidPiva } from "@/lib/validators";
import OrgAdminFlags from "@/components/OrgAdminFlags";

const EMPTY = {
  ragione_sociale: "", piva: "", codice_fiscale: "", sede_legale: "", codice_ateco: "", codice_ateco_secondario: "",
  indirizzo: "", cap: "", comune: "", provincia: "",
  ref_titolo: "", ref_nome: "", ref_cognome: "", ref_ruolo: "",
  n_dipendenti: 0, fatturato: 0, anno_riferimento: new Date().getFullYear(), referente: "",
  forma_giuridica: "", numero_rea: "", descrizione_attivita: "", sito_web: "", pec: "",
  referente_nome: "", referente_ruolo: "", referente_email: "", referente_telefono: "",
  sedi_operative: [], ula: 0, totale_attivo: 0,
  cluster_proposto: "", obbligo_csrd: false, redige_bilancio_sostenibilita: false,
  adotta_vsme: false, certificazioni_esg: "",
};

export default function Organizations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [assessmentsByOrg, setAssessmentsByOrg] = useState({});
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const canWrite = hasRole(user, "admin", "valuatore");

  // ERP verification state (blocco duro su creazione nuova azienda)
  const [erpChecking, setErpChecking] = useState(false);
  const [erpVerified, setErpVerified] = useState(false);
  const [erpClientId, setErpClientId] = useState("");
  const [erpError, setErpError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, asRes, clRes] = await Promise.all([
        api.get("/organizations"),
        api.get("/assessments"),
        api.get("/pillar-weights"),
      ]);
      setOrgs(orgsRes.data);
      setClusters(clRes.data || []);
      const map = {};
      (asRes.data || []).forEach((a) => {
        (map[a.organization_id] ||= []).push(a);
      });
      setAssessmentsByOrg(map);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // n_dipendenti = sum of sedi addetti (auto)
  const sediSum = useMemo(
    () => (form.sedi_operative || []).reduce((acc, s) => acc + (Number(s.addetti) || 0), 0),
    [form.sedi_operative]
  );
  useEffect(() => {
    if (sediSum !== form.n_dipendenti) {
      setForm((f) => ({ ...f, n_dipendenti: sediSum }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sediSum]);

  function openCreate() {
    setForm(EMPTY);
    setEditingId(null);
    setErpVerified(false);
    setErpClientId("");
    setErpError("");
    setOpen(true);
  }
  function openEdit(o) {
    setForm({ ...EMPTY, ...o });
    setEditingId(o.id);
    // In modifica bypassa verifica ERP (azienda già registrata prima)
    setErpVerified(true);
    setErpClientId(o.erp_client_id || "");
    setErpError("");
    setOpen(true);
  }

  async function verifyErp() {
    const piva = (form.piva || "").trim();
    if (!piva) {
      setErpError("Inserisci la P.IVA prima di verificare.");
      return;
    }
    setErpChecking(true);
    setErpError("");
    try {
      const { data } = await api.get(`/erp/clients/lookup`, { params: { piva } });
      // Auto-import dei campi disponibili nell'ERP
      setForm((f) => ({
        ...f,
        ragione_sociale: f.ragione_sociale || data.ragione_sociale || "",
        codice_fiscale: f.codice_fiscale || data.codice_fiscale || "",
        codice_ateco: f.codice_ateco || data.codice_ateco || "",
        sede_legale: f.sede_legale || data.sede_legale || "",
        descrizione_attivita: f.descrizione_attivita || data.settore_attivita || "",
        referente_email: f.referente_email || data.email || "",
        referente_telefono: f.referente_telefono || data.telefono || "",
      }));
      setErpVerified(true);
      setErpClientId(data.client_id || "");
      toast.success(`Azienda trovata nell'ERP: ${data.ragione_sociale}`);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      setErpError(detail || "Azienda non trovata nell'ERP.");
      setErpVerified(false);
      setErpClientId("");
    } finally {
      setErpChecking(false);
    }
  }

  const editingOrg = editingId ? orgs.find((o) => o.id === editingId) : null;
  const pivaInvalid = !editingOrg?.is_test && !isValidPiva(form.piva);

  async function save() {
    if (pivaInvalid) {
      toast.error("Partita IVA non valida: servono 11 cifre con carattere di controllo corretto.");
      return;
    }
    if (!editingId && !erpVerified) {
      toast.error("Verifica la P.IVA sull'ERP prima di salvare.");
      return;
    }
    try {
      const payload = { ...form };
      if (erpClientId) payload.erp_client_id = erpClientId;
      if (editingId) await api.patch(`/organizations/${editingId}`, payload);
      else await api.post("/organizations", payload);
      toast.success(editingId ? "Azienda aggiornata" : "Azienda creata");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore di salvataggio");
    }
  }

  async function remove(id) {
    if (!confirm("Eliminare questa azienda?")) return;
    try {
      await api.delete(`/organizations/${id}`);
      toast.success("Azienda eliminata");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Anagrafica</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Aziende</h1>
          <p className="text-slate-500 mt-1">Registro delle organizzazioni soggette ad assessment.</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="new-org-button" className="h-11 px-5 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Nuova Azienda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editingId ? "Modifica Azienda" : "Nuova Azienda"}</DialogTitle>
              </DialogHeader>

              <SectionTitle>A · Identificazione</SectionTitle>

              {!editingId && (
                <ErpVerifyBanner
                  piva={form.piva}
                  onPivaChange={(v) => {
                    setForm({ ...form, piva: v });
                    setErpVerified(false);
                    setErpClientId("");
                    setErpError("");
                  }}
                  onVerify={verifyErp}
                  checking={erpChecking}
                  verified={erpVerified}
                  clientId={erpClientId}
                  error={erpError}
                />
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Ragione sociale *" value={form.ragione_sociale} onChange={(v) => setForm({ ...form, ragione_sociale: v })} testid="org-ragione" full />
                <Field label="Forma giuridica" value={form.forma_giuridica} onChange={(v) => setForm({ ...form, forma_giuridica: v })} testid="org-forma" />
                <Field
                  label="P.IVA"
                  value={form.piva}
                  onChange={(v) => {
                    setForm({ ...form, piva: v });
                    if (!editingId) {
                      setErpVerified(false);
                      setErpClientId("");
                      setErpError("");
                    }
                  }}
                  testid="org-piva"
                />
                {pivaInvalid && (form.piva || "").trim() !== "" && (
                  <div className="sm:col-span-2 -mt-2 text-xs text-red-600" data-testid="org-piva-error">
                    Partita IVA non valida: servono 11 cifre con carattere di controllo corretto.
                  </div>
                )}
                <Field label="Codice fiscale" value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v })} testid="org-cf" />
                <Field label="Numero REA" value={form.numero_rea} onChange={(v) => setForm({ ...form, numero_rea: v })} testid="org-rea" />
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Codice ATECO (primario)
                  </Label>
                  <AtecoCombobox
                    value={form.codice_ateco}
                    onChange={(v) => setForm({ ...form, codice_ateco: v })}
                    testid="org-ateco"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Codice ATECO secondario
                  </Label>
                  <AtecoCombobox
                    value={form.codice_ateco_secondario}
                    onChange={(v) => setForm({ ...form, codice_ateco_secondario: v })}
                    testid="org-ateco-sec"
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Sede legale
                  </Label>
                  <AddressFields
                    value={{
                      indirizzo: form.indirizzo,
                      cap: form.cap,
                      comune: form.comune,
                      provincia: form.provincia,
                    }}
                    onChange={(next) => setForm({ ...form, ...next })}
                    testidPrefix="org-sede"
                  />
                  {form.sede_legale && !form.indirizzo && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <b>Formato precedente:</b> {form.sede_legale} — compila i
                      campi sopra per aggiornare al nuovo formato strutturato.
                    </p>
                  )}
                  <div className="pt-2 border-t border-slate-100">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
                      Referente di sede legale
                    </Label>
                    <ContactFields
                      value={{
                        ref_titolo: form.ref_titolo,
                        ref_nome: form.ref_nome,
                        ref_cognome: form.ref_cognome,
                        ref_ruolo: form.ref_ruolo,
                      }}
                      onChange={(next) => setForm({ ...form, ...next })}
                      testidPrefix="org-ref-sede"
                    />
                  </div>
                </div>
                <Field label="Descrizione attività prevalente" value={form.descrizione_attivita} onChange={(v) => setForm({ ...form, descrizione_attivita: v })} testid="org-attivita" full />
                <Field label="Sito web" value={form.sito_web} onChange={(v) => setForm({ ...form, sito_web: v })} testid="org-web" />
                <Field label="PEC" value={form.pec} onChange={(v) => setForm({ ...form, pec: v })} testid="org-pec" />
              </div>

              <SectionTitle>B · Referente per la certificazione</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome e cognome" value={form.referente_nome} onChange={(v) => setForm({ ...form, referente_nome: v })} testid="org-ref-nome" />
                <Field label="Ruolo / funzione" value={form.referente_ruolo} onChange={(v) => setForm({ ...form, referente_ruolo: v })} testid="org-ref-ruolo" />
                <Field label="Email" value={form.referente_email} onChange={(v) => setForm({ ...form, referente_email: v })} testid="org-ref-email" />
                <Field label="Telefono diretto" value={form.referente_telefono} onChange={(v) => setForm({ ...form, referente_telefono: v })} testid="org-ref-tel" />
                <Field label="Referente generico (legacy)" value={form.referente} onChange={(v) => setForm({ ...form, referente: v })} testid="org-ref" full />
              </div>

              <SectionTitle>C · Sedi operative oggetto di valutazione</SectionTitle>
              <SediOperativeEditor value={form.sedi_operative || []} onChange={(s) => setForm({ ...form, sedi_operative: s })} />

              <SectionTitle>D · Dati dimensionali ed economici</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    N. dipendenti totale <span className="text-viva-600 normal-case">(auto: somma sedi)</span>
                  </Label>
                  <Input
                    type="number" readOnly value={form.n_dipendenti ?? 0}
                    data-testid="org-dip"
                    className="mt-1.5 h-10 rounded-lg border-slate-200 bg-slate-50 text-slate-700 cursor-not-allowed"
                  />
                </div>
                <Field label="ULA (Unità Lavorative Annue)" type="number" value={form.ula} onChange={(v) => setForm({ ...form, ula: Number(v) })} testid="org-ula" />
                <EuroField label="Fatturato annuo (€)" value={form.fatturato} onChange={(v) => setForm({ ...form, fatturato: v })} testid="org-fatt" />
                <EuroField label="Totale attivo di bilancio (€)" value={form.totale_attivo} onChange={(v) => setForm({ ...form, totale_attivo: v })} testid="org-attivo" />
                <Field label="Esercizio di riferimento (anno)" type="number" value={form.anno_riferimento} onChange={(v) => setForm({ ...form, anno_riferimento: Number(v) })} testid="org-anno" />
              </div>

              <SectionTitle>E · Inquadramento ESG</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs font-medium text-slate-600">Cluster settoriale proposto</Label>
                  <Select
                    value={form.cluster_proposto || ""}
                    onValueChange={(v) => setForm({ ...form, cluster_proposto: v })}
                  >
                    <SelectTrigger data-testid="org-cluster" className="h-10 rounded-lg">
                      <SelectValue placeholder="Seleziona un cluster..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {clusters.map((c) => (
                        <SelectItem key={c.cluster_code} value={c.cluster_code}>
                          {c.cluster_code} — {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <BoolField label="Soggetta a obbligo CSRD?" value={form.obbligo_csrd} onChange={(v) => setForm({ ...form, obbligo_csrd: v })} testid="org-csrd" />
                <BoolField label="Redige bilancio di sostenibilità?" value={form.redige_bilancio_sostenibilita} onChange={(v) => setForm({ ...form, redige_bilancio_sostenibilita: v })} testid="org-bsost" />
                <BoolField label="Adotta lo standard VSME?" value={form.adotta_vsme} onChange={(v) => setForm({ ...form, adotta_vsme: v })} testid="org-vsme" />
                <Field label="Certificazioni ESG-related detenute" value={form.certificazioni_esg} onChange={(v) => setForm({ ...form, certificazioni_esg: v })} testid="org-cert" full />
              </div>

              {editingOrg && hasRole(user, "admin") && <OrgAdminFlags org={editingOrg} onChanged={load} />}

              <DialogFooter className="mt-6">
                <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
                <Button
                  onClick={save}
                  data-testid="save-org-button"
                  disabled={(!editingId && !erpVerified) || pivaInvalid}
                  className="bg-viva-500 hover:bg-viva-600 text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {editingId ? "Salva modifiche" : "Crea"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>
      ) : orgs.length === 0 ? (
        <Card className="p-12 rounded-2xl border-slate-100 shadow-soft text-center text-slate-500">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.25} />
          <p className="font-medium text-slate-700">Nessuna azienda registrata</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {orgs.map((o) => (
            <Card key={o.id} data-testid={`org-card-${o.id}`} className="p-6 rounded-2xl border-slate-100 shadow-soft shadow-hover">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-viva-50 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-viva-600" strokeWidth={1.75} />
                </div>
                {canWrite && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(o)} data-testid={`org-edit-${o.id}`}><Edit2 className="w-4 h-4" /></Button>
                    {hasRole(user, "admin") && (
                      <Button variant="ghost" size="icon" onClick={() => remove(o.id)} data-testid={`org-delete-${o.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    )}
                  </div>
                )}
              </div>
              <div className="font-heading font-semibold text-lg text-slate-900 mb-1">
                {o.ragione_sociale}
                {o.is_test && <span className="ml-2 align-middle px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600" data-testid={`org-test-badge-${o.id}`}>TEST</span>}
              </div>
              {(o.ateco_warnings || []).map((w) => (
                <div key={w} className="mb-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-1" data-testid={`org-ateco-warning-${o.id}`}>{w}</div>
              ))}
              <div className="text-xs text-slate-500 space-y-1">
                {o.piva && <div>P.IVA: {o.piva}</div>}
                {o.codice_ateco && <div>ATECO: {o.codice_ateco}{o.codice_ateco_secondario ? ` / ${o.codice_ateco_secondario}` : ""}</div>}
                {o.n_dipendenti ? <div>{plural(o.n_dipendenti, "dipendente", "dipendenti")}</div> : null}
                {o.referente && <div>Referente: {o.referente}</div>}
              </div>
              {(() => {
                const orgAs = (assessmentsByOrg[o.id] || []).sort(
                  (a, b) => (b.year || 0) - (a.year || 0)
                );
                if (orgAs.length === 0) {
                  return canWrite ? (
                    <Button
                      variant="outline" size="sm"
                      className="mt-4 w-full border-viva-200 text-viva-700 hover:bg-viva-50"
                      onClick={() => navigate(`/assessment/nuovo?org=${o.id}`)}
                      data-testid={`org-new-assessment-${o.id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Crea assessment
                    </Button>
                  ) : null;
                }
                const latest = orgAs[0];
                const target = canWrite
                  ? `/assessment/${latest.id}/compila`
                  : `/assessment/${latest.id}/risultati`;
                return (
                  <div className="mt-4 space-y-2">
                    <Button
                      variant="outline" size="sm"
                      className="w-full border-viva-200 text-viva-700 hover:bg-viva-50 group"
                      onClick={() => navigate(target)}
                      data-testid={`org-go-assessment-${o.id}`}
                    >
                      <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
                      Assessment {latest.year || ""} · {latest.profile_level}
                      {orgAs.length > 1 ? ` (+${orgAs.length - 1})` : ""}
                      <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-60 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="w-full text-slate-600 hover:text-viva-700 hover:bg-viva-50/60"
                      onClick={() => navigate(`/aziende/${o.id}/trend`)}
                      data-testid={`org-trend-${o.id}`}
                    >
                      <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                      Trend pluriennale
                    </Button>
                  </div>
                );
              })()}
              <OrgClienteUsers orgId={o.id} orgName={o.ragione_sociale} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ErpVerifyBanner({ piva, onPivaChange, onVerify, checking, verified, clientId, error }) {
  const trimmedPiva = (piva || "").trim();
  return (
    <div
      className={`mb-5 rounded-xl border p-4 ${
        verified
          ? "border-emerald-200 bg-emerald-50"
          : error
          ? "border-rose-200 bg-rose-50"
          : "border-viva-200 bg-viva-50/50"
      }`}
      data-testid="erp-verify-banner"
    >
      <div className="flex items-start gap-3">
        {verified ? (
          <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
        ) : error ? (
          <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
        ) : (
          <Search className="w-5 h-5 text-viva-600 mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {verified ? "Azienda verificata sull'ERP" : "Verifica anagrafica ERP"}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {verified
              ? `Cliente ${clientId} — puoi procedere con il salvataggio.`
              : "Inserisci la P.IVA e verifica prima di creare la scheda azienda."}
          </div>
          {!verified && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 mt-3">
              <div className="flex-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">P.IVA</Label>
                <Input
                  value={trimmedPiva}
                  onChange={(e) => onPivaChange(e.target.value)}
                  placeholder="es. 12345678901"
                  data-testid="erp-verify-piva-input"
                  className="mt-1.5 h-10 rounded-lg"
                />
              </div>
              <Button
                type="button"
                onClick={onVerify}
                disabled={checking || !trimmedPiva}
                data-testid="erp-verify-btn"
                className="h-10 bg-viva-500 hover:bg-viva-600 text-white disabled:bg-slate-300"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifica su ERP"}
              </Button>
            </div>
          )}
          {error && (
            <div className="text-xs font-medium text-rose-700 mt-2" data-testid="erp-verify-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 h-10 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500"
      />
    </div>
  );
}

// Currency input that renders as type=text (no spinner arrows), formats with
// thousand separators + € prefix, and emits a numeric value on change.
function EuroField({ label, value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  useEffect(() => {
    const num = Number(value || 0);
    setDraft(num ? num.toLocaleString("it-IT") : "");
  }, [value]);
  function handleChange(raw) {
    // keep only digits, comma, dot
    const cleaned = raw.replace(/[^0-9.,]/g, "").replace(/\./g, "").replace(",", ".");
    const num = Number(cleaned);
    if (!isNaN(num)) onChange(num);
    setDraft(raw.replace(/[^0-9.,\s]/g, ""));
  }
  function handleBlur() {
    const num = Number(value || 0);
    setDraft(num ? num.toLocaleString("it-IT") : "");
  }
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="relative mt-1.5">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium pointer-events-none">€</span>
        <Input
          type="text" inputMode="decimal"
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          data-testid={testid}
          className="h-10 pl-7 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500 tabular-nums"
          placeholder="0"
        />
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mt-6 mb-3 pb-2 border-b border-slate-100">
      <h3 className="font-heading font-semibold text-slate-900 text-base">{children}</h3>
    </div>
  );
}

function BoolField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5 flex gap-2">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(v)}
            data-testid={`${testid}-${v ? "yes" : "no"}`}
            className={`flex-1 h-10 px-4 rounded-lg text-sm font-medium border transition-colors ${value === v ? "bg-viva-500 text-white border-viva-500" : "bg-white text-slate-600 border-slate-200 hover:border-viva-300"}`}
          >
            {v ? "SÌ" : "NO"}
          </button>
        ))}
      </div>
    </div>
  );
}

function SediOperativeEditor({ value, onChange }) {
  function add() {
    onChange([
      ...(value || []),
      { indirizzo: "", cap: "", comune: "", provincia: "",
        ref_titolo: "", ref_nome: "", ref_cognome: "", ref_ruolo: "",
        attivita: "", addetti: 0 },
    ]);
  }
  function update(idx, key, v) {
    const next = [...value];
    next[idx] = { ...next[idx], [key]: v };
    onChange(next);
  }
  function updateAddr(idx, addr) {
    const next = [...value];
    next[idx] = { ...next[idx], ...addr };
    onChange(next);
  }
  function updateRef(idx, ref) {
    const next = [...value];
    next[idx] = { ...next[idx], ...ref };
    onChange(next);
  }
  function remove(idx) {
    onChange(value.filter((_, i) => i !== idx));
  }
  return (
    <div className="space-y-3" data-testid="sedi-editor">
      {(value || []).map((s, i) => (
        <div key={i} className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid={`sede-row-${i}`}>
          <AddressFields
            value={{
              indirizzo: s.indirizzo,
              cap: s.cap,
              comune: s.comune,
              provincia: s.provincia,
            }}
            onChange={(a) => updateAddr(i, a)}
            testidPrefix={`sede-${i}`}
          />
          <div className="pt-2 border-t border-slate-200">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
              Referente della sede
            </Label>
            <ContactFields
              value={{
                ref_titolo: s.ref_titolo,
                ref_nome: s.ref_nome,
                ref_cognome: s.ref_cognome,
                ref_ruolo: s.ref_ruolo,
              }}
              onChange={(r) => updateRef(i, r)}
              testidPrefix={`sede-${i}-ref`}
            />
          </div>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-8">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Attività svolte</Label>
              <Input value={s.attivita || ""} onChange={(e) => update(i, "attivita", e.target.value)} className="mt-1 h-9 rounded-md border-slate-200 bg-white" />
            </div>
            <div className="col-span-8 sm:col-span-3">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Addetti</Label>
              <Input
                type="number" min="0"
                value={s.addetti ?? 0}
                onChange={(e) => update(i, "addetti", Number(e.target.value))}
                className="mt-1 h-9 rounded-md border-slate-200 bg-white text-center"
                data-testid={`sede-addetti-${i}`}
              />
            </div>
            <div className="col-span-4 sm:col-span-1 flex justify-end items-end pb-1">
              <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`sede-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-sede-button" className="w-full border-dashed border-slate-300 text-slate-600">
        <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi sede operativa
      </Button>
    </div>
  );
}
